import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './locales/en/translation.json';
import hi from './locales/hi/translation.json';
import ta from './locales/ta/translation.json';
import te from './locales/te/translation.json';
import mr from './locales/mr/translation.json';
import gu from './locales/gu/translation.json';
import bn from './locales/bn/translation.json';
import kn from './locales/kn/translation.json';
import ml from './locales/ml/translation.json';
import pa from './locales/pa/translation.json';
import or from './locales/or/translation.json';
import as from './locales/as/translation.json';
import ur from './locales/ur/translation.json';
import { normalizeLanguage, SUPPORTED_LANGUAGES } from './languages';
import { BNN_MODULE_TRANSLATIONS } from './bnnModuleTranslations';
import { applyLocaleAdditions } from './localeAdditions';

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];
export { normalizeLanguage, SUPPORTED_LANGUAGES };

export const LANGUAGE_STORAGE_KEY = 'kundlistarLanguage';
const withBnn = (resource: any, language: string) => applyLocaleAdditions({ ...resource, bnn: { ...(resource.bnn || {}), ...(BNN_MODULE_TRANSLATIONS[language] || BNN_MODULE_TRANSLATIONS.en) } }, language);

const getInitialLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return 'en';

  const urlLanguage = new URLSearchParams(window.location.search).get('lang');
  if (urlLanguage) return normalizeLanguage(urlLanguage);

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (storedLanguage) return normalizeLanguage(storedLanguage);

  return normalizeLanguage(window.navigator.language);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: getInitialLanguage(),
    resources: {
      en: { translation: withBnn(en, 'en') }, hi: { translation: withBnn(hi, 'hi') }, ta: { translation: withBnn(ta, 'ta') },
      te: { translation: withBnn(te, 'te') }, mr: { translation: withBnn(mr, 'mr') }, gu: { translation: withBnn(gu, 'gu') },
      bn: { translation: withBnn(bn, 'bn') }, kn: { translation: withBnn(kn, 'kn') }, ml: { translation: withBnn(ml, 'ml') },
      pa: { translation: withBnn(pa, 'pa') }, or: { translation: withBnn(or, 'or') }, as: { translation: withBnn(as, 'as') }, ur: { translation: withBnn(ur, 'ur') },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    cleanCode: true,
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
      convertDetectedLanguage: normalizeLanguage,
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

const persistLanguage = (language: string) => {
  if (typeof window === 'undefined') return;

  const normalizedLanguage = normalizeLanguage(language);
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
  document.documentElement.lang = normalizedLanguage;
};

persistLanguage(i18n.language);
i18n.on('languageChanged', persistLanguage);

export default i18n;
